import type { INQUIRY_FILTERS, INQUIRY_ITEM, INQUIRY_TYPE } from '@/types'
import { matchInquiryFilterToInquiryType } from '@/utils/matchers'
import { sleep } from '@/utils/parsers'

interface CustomWindow extends Window {
  webkit?: any
}

declare const window: CustomWindow

export function useInquiriesLoader() {
  const route = useRoute()
  const router = useRouter()
  const appStore = useAppStore()
  const authStore = useAuthStore()
  const inquiriesStore = useInquiriesStore()

  const inquiries = ref<INQUIRY_ITEM[]>([])
  const isFiltering = ref(false)

  const hasLoaded = ref(false)

  const currentFilters = (): INQUIRY_FILTERS => ({
    inquiryType: route.query.inquiryType
      ? (route.query.inquiryType as INQUIRY_TYPE)
      : (matchInquiryFilterToInquiryType(
          appStore.inquiryFilter ?? 'All',
        ) as INQUIRY_TYPE),
    filter: (route.query.filter as INQUIRY_FILTERS['filter']) ?? 'Upcoming',
    sort: (route.query.sort as INQUIRY_FILTERS['sort']) ?? 'Received',
  })

  const refresh = async () => {
    const { inquiryType, filter, sort } = currentFilters()
    await inquiriesStore.fetchInquiries(inquiryType, filter, sort)
    inquiries.value = inquiriesStore.inquiries
    hasLoaded.value = true
  }

  const reload = async () => {
    appStore.isLoading = true

    try {
      await refresh()

      if (window.webkit?.messageHandlers?.spotChangeHandler) {
        window.webkit.messageHandlers.spotChangeHandler.postMessage(
          authStore.spotId,
        )
      }
    } catch {
      // A failed list fetch must not leave the app stuck behind the loader.
    } finally {
      appStore.isLoading = false
    }
  }

  const ensureLoaded = async () => {
    if (hasLoaded.value) return
    if (appStore.iosError) return
    if (!appStore.spotInfo) return
    if (!appStore.spotHopperEmbedded && !authStore.isAuthenticated) return

    await reload()
  }

  const handleFilterInquiries = async (filters: INQUIRY_FILTERS) => {
    isFiltering.value = true

    const { inquiryType, filter, sort } = filters
    route.query.inquiryType = inquiryType
    route.query.filter = filter
    route.query.sort = sort

    const { href } = router.resolve(route)
    window.history.pushState({}, '', href)

    await inquiriesStore.fetchInquiries(inquiryType, filter, sort)
    inquiries.value = inquiriesStore.inquiries
    await sleep(500)
    isFiltering.value = false
  }

  watch(
    () => inquiriesStore.inquiries,
    (newValue) => {
      if (!newValue) return
      inquiries.value = newValue
    },
  )

  return {
    inquiries,
    isFiltering,
    hasLoaded,
    ensureLoaded,
    reload,
    refresh,
    handleFilterInquiries,
  }
}
